/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { PrimaryButton } from "@fluentui/react/lib/Button";
import { SearchBox } from "@fluentui/react/lib/SearchBox";
import { Stack } from "@fluentui/react/lib/Stack";
import { Text } from "@fluentui/react/lib/Text";

import * as ClRegionActions from "Dataflows/ClRegion/ClRegionAction";
import * as IndustryFocusActions from "Dataflows/IndustryFocus/IndustryFocusAction";
import * as UserProfilesActions from "Dataflows/RD/UserProfile/RDUserProfileAction";
import * as UserProfileSelectors from "Dataflows/RD/UserProfile/RDUserProfileSelector";
import * as TechnicalExpertiseActions from "Dataflows/TechnicalExpertise/TechnicalExpertiseAction";

import ListCounts from "Components/Common/ListCounts/ListCounts";
import { IRegionalDirectorsListItem } from "Components/RegionalDirectorsList/IRegionalDirectorsListItem";
import { IRegionalDirectorsListProps } from "Components/RegionalDirectorsList/IRegionalDirectorsListProps";
import RegionalDirectorsLists from "Components/RegionalDirectorsList/RegionalDirectorsList";
import { OperationStatus } from "Constants/OperationStatus";
import {
  defaultListingPageSize,
  defaultRdFilterValues,
  rdSidebarFilterFields,
} from "Constants/ProfileConstants";
import { BrandLabel } from "Constants/SiteConstants";
import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  tableStackStyles,
  RDButtonStyles,
  RDFlexRow,
  RDPaddedFlexRow,
  RDTextStyles,
} from "Containers/Common/CommonContainer.styles";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IRDUserProfile } from "Interfaces/RD/UserProfile/IRDUserProfile";
import { constructCsvDownload } from "Utility/DownloadHelper";

import SideBarContainer from "../../SideBarContainer/SideBarContainer";

/**
 * Regional Directors Container.
 * Container for Regional Directors Profile List.
 * @returns {React.ReactElement} rendered container
 */
const RegionalDirectorsContainer = (): React.ReactElement => {
  const dispatch = useDispatch();

  /**
   *  useSelector variables.
   */
  const pageSize: number = useSelector(UserProfileSelectors.getPageSize);
  const currentPage: number = useSelector(UserProfileSelectors.getCurrentPage);
  const sortColumn: string | undefined = useSelector(UserProfileSelectors.getCurrentSortColumn);
  const sortDescending: boolean | undefined = useSelector(
    UserProfileSelectors.getCurrentSortDescending
  );
  const [searchText, setSearchText] = useState(useSelector(UserProfileSelectors.getSearchText));
  const isUserProfilesLoading: boolean = useSelector(UserProfileSelectors.isUserProfilesLoading);
  const userProfiles: IRDUserProfile[] = useSelector(UserProfileSelectors.getUserProfiles);
  const selectedUserProfilesFilterValues: IFilterValue[] = useSelector(
    UserProfileSelectors.getUserProfilesFilterValues
  );
  const filteredCount: number = useSelector(UserProfileSelectors.getFilteredCount);
  const fileDownloadData: string | undefined = useSelector(
    UserProfileSelectors.getFileDownloadData
  );
  const fileDownloadStatus: OperationStatus = useSelector(
    UserProfileSelectors.getFileDownloadStatus
  );

  useEffect(() => {
    dispatch(ClRegionActions.getClRegions());
    dispatch(IndustryFocusActions.getIndustryFocus());
    dispatch(TechnicalExpertiseActions.getTechnicalExpertise());
  }, []);

  useEffect(() => {
    if (fileDownloadStatus === OperationStatus.Succeeded && !!fileDownloadData) {
      constructCsvDownload(fileDownloadData, `rds_${new Date().toLocaleString()}.csv`);
      dispatch(UserProfilesActions.resetFileDownloadStatus());
    }
  }, [fileDownloadStatus, fileDownloadData]);

  useEffect(() => {
    dispatch(
      UserProfilesActions.searchRDUserProfiles(
        searchText,
        selectedUserProfilesFilterValues,
        currentPage,
        pageSize,
        sortColumn,
        sortDescending
      )
    );
  }, [currentPage, pageSize, sortColumn, sortDescending]);

  /**
   * Call search function for entered text.
   * @method
   * @param {string} searchValue The search text.
   * @param {IFilterValue[]} selectedUserProfilesFilterValues The filter values.
   * @param {number} page The page number.
   * @returns {void}
   */
  const handleSearch = (
    searchValue: string,
    selectedUserProfilesFilterValues: IFilterValue[],
    page: number,
    pageSize: number,
    sortColumn?: string,
    sortDescending?: boolean
  ): void => {
    dispatch(
      UserProfilesActions.searchRDUserProfiles(
        searchValue,
        selectedUserProfilesFilterValues,
        page,
        pageSize,
        sortColumn,
        sortDescending
      )
    );
  };

  /**
   * Call to reset filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    dispatch(UserProfilesActions.userProfileFilterValueReset());

    setSearchText("");
    dispatch(
      UserProfilesActions.searchRDUserProfiles("", defaultRdFilterValues, 1, defaultListingPageSize)
    );
  };

  /**
   * Call to handle the onClear action.
   */
  const onClear = (_: any): void => {
    dispatch(
      UserProfilesActions.searchRDUserProfiles("", selectedUserProfilesFilterValues, 1, pageSize)
    );
  };

  /**
   * Call to handle the download to csv action.
   */
  const handleDownloadToCSV = (): void => {
    dispatch(
      UserProfilesActions.downloadRDUserProfilesToCSV(searchText, selectedUserProfilesFilterValues)
    );
  };

  const profiles: IRegionalDirectorsListItem[] = userProfiles?.map((item) => {
    return {
      id: item.id,
      firstName: item.firstName,
      lastName: item.lastName,
      primaryEmail: item.primaryEmail,
      awardPeriodStartDate: item.awardPeriodStartDate,
      awardPeriodEndDate: item.awardPeriodEndDate,
      programProfileStatus: item.programProfileStatus,
      applicationProfileStatus: item.applicationStatus,
      cpm: item.cpm,
    };
  });

  const addFilterValue = (filter: IFilterValue) =>
    dispatch(UserProfilesActions.addUserProfilesFilterValue(filter));
  const removeUserProfilesFilterValue = (filter: IFilterValue) =>
    dispatch(UserProfilesActions.removeUserProfilesFilterValue(filter));
  const updateUserProfilesFilterValue = (filter: IFilterValue) =>
    dispatch(UserProfilesActions.updateUserProfilesFilterValue(filter));
  const removeUserProfilesProvinceFilterValue = () =>
    dispatch(UserProfilesActions.removeUserProfilesFilterProvinceValue());

  const rdListsProps: IRegionalDirectorsListProps = {
    data: profiles,
    filteredCount,
    currentPage,
    pageSize,
    fetchSelectedUserProfile: (rdUserProfileId: number) =>
      dispatch(UserProfilesActions.fetchUserProfile(rdUserProfileId)),
  };

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          addFilterValue={addFilterValue}
          removeFilterValue={removeUserProfilesFilterValue}
          updateFilterValue={updateUserProfilesFilterValue}
          removeFilterProvinceValue={removeUserProfilesProvinceFilterValue}
          selectedFilterValues={selectedUserProfilesFilterValues}
          filterFields={rdSidebarFilterFields}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void =>
              handleSearch(
                searchText,
                selectedUserProfilesFilterValues,
                1,
                pageSize,
                sortColumn,
                sortDescending
              )
            }
          />
          <PrimaryButton styles={filterButtonStyles()} text="Reset" onClick={resetFilterValues} />
        </Stack>
      </Stack.Item>
      <Stack.Item grow styles={listStackStyles()}>
        <Stack styles={tableStackStyles}>
          <RDPaddedFlexRow>
            <Text styles={RDTextStyles}>{BrandLabel.RD}</Text>
            <RDFlexRow>
              <ListCounts total={filteredCount} loading={isUserProfilesLoading} />
              <PrimaryButton
                text="Download to CSV"
                styles={RDButtonStyles}
                iconProps={{ iconName: "download" }}
                onClick={handleDownloadToCSV}
              />
            </RDFlexRow>
          </RDPaddedFlexRow>
          <SearchBox
            placeholder="Search regional directors"
            value={searchText}
            onSearch={(searchValue: string): void =>
              handleSearch(searchValue, selectedUserProfilesFilterValues, currentPage, pageSize)
            }
            onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
              setSearchText(newValue || "")
            }
            onClear={onClear}
            styles={searchBoxStyles}
          />
          <RegionalDirectorsLists {...rdListsProps} />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};

export default RegionalDirectorsContainer;
